// The app-wide security-PIN unlock. Entering the PIN on any PIN-gated surface
// unlocks every PIN-gated surface for a user-chosen window.
//
// What is stored is the server's unlock grant (a short-lived token from
// POST /v1/user/verify-pin), never the PIN itself — the grant is what the API
// actually checks, and it expires on its own even if this session outlives it.
const SESSION_KEY = "reppi_pin_unlock_session_v2";
// The v1 session held the raw PIN in sessionStorage; drop any that linger.
const LEGACY_SESSION_KEY = "reppi_pin_unlock_session_v1";

export const PIN_TOKEN_HEADER = "X-Pin-Token";

export const PIN_UNLOCK_DURATIONS = [
  { value: 1, label: "1 minute" },
  { value: 5, label: "5 minutes" },
  { value: 15, label: "15 minutes" },
  { value: 30, label: "30 minutes" },
  { value: 60, label: "1 hour" },
];

export const DEFAULT_PIN_UNLOCK_DURATION = 5;

const canUseStorage = () =>
  typeof window !== "undefined" && typeof window.sessionStorage !== "undefined";

const readSession = () => {
  if (!canUseStorage()) return null;

  try {
    window.sessionStorage.removeItem(LEGACY_SESSION_KEY);

    const raw = window.sessionStorage.getItem(SESSION_KEY);
    if (!raw) return null;

    const parsed = JSON.parse(raw);
    if (
      !parsed?.token ||
      !parsed?.expiresAt ||
      Date.now() >= parsed.expiresAt
    ) {
      clearPinUnlockSession();
      return null;
    }

    return parsed;
  } catch {
    return null;
  }
};

// `expiresIn` is the grant's own lifetime in seconds, as reported by the server.
// The session never outlives it, whatever window the user picked.
export const startPinUnlockSession = ({
  token,
  expiresIn,
  durationMinutes,
} = {}) => {
  if (!canUseStorage() || !token) return;

  const requested =
    (Number(durationMinutes) || DEFAULT_PIN_UNLOCK_DURATION) * 60 * 1000;
  const granted = Number(expiresIn) > 0 ? Number(expiresIn) * 1000 : requested;
  const now = Date.now();

  window.sessionStorage.setItem(
    SESSION_KEY,
    JSON.stringify({
      verifiedAt: now,
      expiresAt: now + Math.min(requested, granted),
      token,
    })
  );
};

export const clearPinUnlockSession = () => {
  if (!canUseStorage()) return;
  window.sessionStorage.removeItem(SESSION_KEY);
  window.sessionStorage.removeItem(LEGACY_SESSION_KEY);
};

export const isPinUnlockSessionActive = () => Boolean(readSession());

export const getPinToken = () => readSession()?.token || null;

export const getRemainingPinUnlockMs = () => {
  const session = readSession();
  if (!session) return 0;
  return Math.max(0, session.expiresAt - Date.now());
};

// Spread into a fetch's headers to send the unlock on a pay endpoint. Yields an
// empty object when locked, so the request goes out and the server rejects it —
// the server, not this file, is what enforces the PIN.
export const pinUnlockHeaders = () => {
  const token = getPinToken();
  return token ? { [PIN_TOKEN_HEADER]: token } : {};
};

// True when a pay endpoint refused the request for want of a PIN unlock. Clears
// the session so the UI re-locks and prompts again.
export const isPinRequiredResponse = (response, body) => {
  const required =
    response?.status === 403 &&
    (body?.code === "PIN_REQUIRED" || body?.message?.code === "PIN_REQUIRED");

  if (required) clearPinUnlockSession();
  return required;
};
